import React, { useState, useCallback, useEffect } from 'react';
import { Edit3, Check, X, Loader2, RefreshCw, Eye, AlertCircle } from 'lucide-react';
import { Button, Card } from '../ui';
import { morningPagesService, type MorningPageDto } from '../../services';
import './OcrReviewEdit.css';

interface OcrReviewEditProps {
  morningPage: MorningPageDto;
  onOcrUpdate?: (updatedPage: MorningPageDto) => void;
  onAnalyze?: () => void;
  readOnly?: boolean;
}

export const OcrReviewEdit: React.FC<OcrReviewEditProps> = ({
  morningPage,
  onOcrUpdate,
  onAnalyze,
  readOnly = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState(morningPage.ocrText || '');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showOriginal, setShowOriginal] = useState(false);
  const [originalText, setOriginalText] = useState(morningPage.ocrText || '');

  useEffect(() => {
    setEditedText(morningPage.ocrText || '');
    setOriginalText(morningPage.ocrText || '');
  }, [morningPage.ocrText]);

  const handleProcessOcr = useCallback(async (forceReprocess = false) => {
    setIsProcessing(true);
    setError(null);

    try {
      const updatedPage = await morningPagesService.processOcr(morningPage.id, forceReprocess);
      setEditedText(updatedPage.ocrText || '');
      setOriginalText(updatedPage.ocrText || '');
      onOcrUpdate?.(updatedPage);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process OCR');
    } finally {
      setIsProcessing(false);
    }
  }, [morningPage.id, onOcrUpdate]);

  const handleSave = useCallback(async (triggerReanalysis = false) => {
    if (editedText === originalText) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const updatedPage = await morningPagesService.updateOcrText(morningPage.id, {
        ocrText: editedText,
        triggerReanalysis,
      });
      setOriginalText(editedText);
      setIsEditing(false);
      onOcrUpdate?.(updatedPage);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save OCR text');
    } finally {
      setIsSaving(false);
    }
  }, [morningPage.id, editedText, originalText, onOcrUpdate]);

  const handleCancel = useCallback(() => {
    setEditedText(originalText);
    setIsEditing(false);
    setError(null);
  }, [originalText]);

  const hasText = editedText.trim().length > 0;
  const hasChanges = editedText !== originalText;

  return (
    <Card variant="outlined" padding="md" className="ocr-review">
      <div className="ocr-review__header">
        <h3 className="ocr-review__title">
          <Edit3 size={18} />
          Extracted Text
        </h3>
        <div className="ocr-review__actions">
          {!morningPage.hasOcrText && !isProcessing && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => handleProcessOcr(false)}
              disabled={isProcessing}
            >
              <RefreshCw size={16} />
              Extract Text
            </Button>
          )}
          {morningPage.hasOcrText && !isEditing && !readOnly && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleProcessOcr(true)}
                disabled={isProcessing}
                title="Re-run OCR extraction"
              >
                <RefreshCw size={16} />
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setIsEditing(true)}
              >
                <Edit3 size={16} />
                Edit
              </Button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="ocr-review__error">
          <AlertCircle size={16} />
          <span>{error}</span>
          <button onClick={() => setError(null)} aria-label="Dismiss error">
            <X size={14} />
          </button>
        </div>
      )}

      {isProcessing ? (
        <div className="ocr-review__processing">
          <Loader2 size={32} className="ocr-review__spinner" />
          <p>Extracting text from your morning pages...</p>
          <p className="ocr-review__hint">This may take a moment</p>
        </div>
      ) : !hasText && !isEditing ? (
        <div className="ocr-review__empty">
          <p>No text has been extracted yet.</p>
          <p className="ocr-review__hint">
            Click "Extract Text" to analyze your handwriting using AI-powered OCR.
          </p>
        </div>
      ) : isEditing ? (
        <div className="ocr-review__edit-mode">
          <div className="ocr-review__toggle">
            <button
              className={`ocr-review__toggle-btn ${!showOriginal ? 'ocr-review__toggle-btn--active' : ''}`}
              onClick={() => setShowOriginal(false)}
            >
              <Edit3 size={14} />
              Edited
            </button>
            <button
              className={`ocr-review__toggle-btn ${showOriginal ? 'ocr-review__toggle-btn--active' : ''}`}
              onClick={() => setShowOriginal(true)}
            >
              <Eye size={14} />
              Original
            </button>
          </div>

          {showOriginal ? (
            <div className="ocr-review__text ocr-review__text--readonly">
              {originalText || 'No original text available'}
            </div>
          ) : (
            <textarea
              className="ocr-review__textarea"
              value={editedText}
              onChange={(e) => setEditedText(e.target.value)}
              placeholder="Edit the extracted text here..."
              rows={10}
              disabled={isSaving}
            />
          )}

          <div className="ocr-review__edit-actions">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              disabled={isSaving}
            >
              <X size={16} />
              Cancel
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleSave(false)}
              disabled={isSaving || !hasChanges}
              loading={isSaving}
            >
              <Check size={16} />
              Save
            </Button>
            {onAnalyze && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => handleSave(true)}
                disabled={isSaving}
                loading={isSaving}
              >
                <Check size={16} />
                Save & Re-analyze
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="ocr-review__text">
          {editedText}
        </div>
      )}

      {hasText && !isEditing && !isProcessing && (
        <div className="ocr-review__footer">
          <span className="ocr-review__word-count">
            {editedText.split(/\s+/).filter(Boolean).length} words
          </span>
          {onAnalyze && morningPage.hasOcrText && !morningPage.hasAnalysis && (
            <Button
              variant="primary"
              size="sm"
              onClick={onAnalyze}
            >
              Analyze with AI
            </Button>
          )}
        </div>
      )}
    </Card>
  );
};

export default OcrReviewEdit;
